import React, { useState } from 'react';
import { Box, Container, Grid, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import { projectbannerbg } from '../../../../../assets';
import { AboutSection } from '../ProjectInterface';

interface About3Props {
  aboutsection: AboutSection;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const Image = styled('img')({});

const ImageGallery: React.FC<About3Props> = ({ aboutsection }) => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        // height: '100vh',
        backgroundImage: `url(${projectbannerbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'bottom center',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 6,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ color: 'white' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'white',
              textAlign: 'start',
            }}
          >
            ABOUT PALATIUM
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '18px',
              textAlign: 'justify',
              fontWeight: 300,
              letterSpacing: '0.8px',
              color: 'white',
            }}
          >
            {aboutsection.des}
          </Typography>
        </Box>

        {/* Image gallery */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: 300,
            gap: '10px',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 2,
            mt: 4,
          }}
          onMouseLeave={() => setHovered(null)}
        >
          {aboutsection.images?.map((img, index) => (
            <Image
              key={index}
              src={resolveImageUrl(img.img)}
              alt={img.h1 || `Gallery ${index + 1}`}
              onMouseEnter={() => setHovered(index)}
              className={
                hovered === null
                  ? index === 0
                    ? 'expanded'
                    : ''
                  : hovered === index
                  ? 'expanded'
                  : ''
              }
              style={{
                width: hovered === index || (hovered === null && index === 0) ? '50%' : '10%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '10px',
                border: '2px solid #fff',
                transition: 'all ease-out 0.5s',
                cursor: 'pointer',
                zIndex: hovered === index ? 2 : 1,
              }}
            />
          ))}
        </Box>

        {/* Stats section */}
        <Grid
          container
          sx={{
            textAlign: 'center',
            position: 'relative',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          {aboutsection.highlights?.map((item, index) => (
            <Grid
              item
              xs={6}
              md={3}
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexDirection: 'column',
                pb: isMobile ? 4 : 0,
                pt: isMobile ? 4 : 0,
                px: 0.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  color: 'white',
                  gap: 0.2,
                  textAlign: 'center',
                  width: '100%',
                  ...(isMobile && {
                    justifyContent: 'center',
                    textAlign: 'center',
                    whiteSpace: 'nowrap', // prevent wrapping
                  }),
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '36px',
                    color: 'white',
                    lineHeight: 1.1,
                  }}
                >
                  {item.h1}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: 300,
                    letterSpacing: 1,
                    color: 'white',
                    lineHeight: 1.1,
                    mt: 0.5,
                  }}
                >
                  {item.d1}
                </Typography>
              </Box>

              {/* Vertical divider */}
              {index !== aboutsection.highlights?.length - 1 &&
                !(isMobile && index === 1) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '-2px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '40px',
                      width: '1px',
                      background: 'white',
                      opacity: 0.5,
                    }}
                  />
                )}
              {/* Horizontal divider on mobile */}
              {isMobile &&
                index % 2 !== 0 &&
                index !== aboutsection.highlights?.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: '0%',
                      transform: 'translateX(-25%)',
                      height: '1px',
                      width: '500px',
                      background: 'white',
                      opacity: 0.5,
                      
                    }}
                  />
                )}
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ImageGallery;
